import React, { useState, useEffect, useCallback, useRef } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import AsyncCreatableSelect from "react-select/async-creatable";
import { customFetch } from '../apiClient';

// ✅ COMPACT STYLES FOR REACT SELECT (26px height like Discharge Form)
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '26px',
    height: '26px',
    fontSize: '11px',
    borderRadius: '4px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 2px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    padding: '0'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px', height: '24px' }),
  indicatorsContainer: (base) => ({ ...base, height: '24px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
  input: (base) => ({ ...base, margin: '0', padding: '0', fontSize: '11px' }),
  menu: (base) => ({ ...base, zIndex: 99999, borderRadius: '4px' }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  singleValue: (base) => ({
    ...base,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    fontSize: '11px'
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    padding: '6px 10px',
    backgroundColor: state.isSelected 
      ? 'var(--theme-color, #0088cc)' 
      : state.isFocused 
        ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
        : 'white',
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' })
};

const payModeOptions = [
  { value: "Cash", label: "Cash" },
  { value: "UPI", label: "UPI" },
  { value: "Cheque", label: "Cheque" },
  { value: "Other", label: "Other" }
];

const ExPDTSearchForm = () => {
  const [formData, setFormData] = useState({
    fromDate: "", toDate: "", accountName: "", bankName: "",
  });

  const [allExpenses, setAllExpenses] = useState([]);
  const [displayExpenses, setDisplayExpenses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalRecords, setTotalRecords] = useState(0);
  const [latestEntryId, setLatestEntryId] = useState(null);

  const [accountOptions, setAccountOptions] = useState([]);
  const [bankOptions, setBankOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [rawAccounts, setRawAccounts] = useState([]);
  const [rawBanks, setRawBanks] = useState([]);
  const editFormRef = useRef(null);

  // Edit Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    expID: "", amID: "", bid: "", accountName: "", bankName: "",
    amount: "", exDate: "", payMode: "Cash", chqNo: "", chqDate: "", remarks: "",
    patientName: "", doctorName: "", patientType: "", refId: null, patientFullLabel: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const token = sessionStorage.getItem("token");

  useEffect(() => {
    if (success) { const timer = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(timer); }
  }, [success]);

  useEffect(() => {
    if (error) { const timer = setTimeout(() => setError(""), 4000); return () => clearTimeout(timer); }
  }, [error]);

  const applyPagination = (data, page, rows) => {
    const startIndex = (page - 1) * rows;
    const endIndex = startIndex + rows;
    setDisplayExpenses(data.slice(startIndex, endIndex));
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    let value = parseInt(e.target.value);
    if (isNaN(value) || value < 1) value = 15;
    if (value > 100) value = 100;
    setRowsPerPage(value); setCurrentPage(1); applyPagination(allExpenses, 1, value);
  };

  // KEYBOARD NAVIGATION FOR EDIT MODAL
  useEffect(() => {
    if (!showEditModal) return; 

    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (e.target.closest('.react-select__menu')) return;

      const focusableSelector = 
        'input:not([disabled]):not([type="hidden"]):not([readonly]), ' +
        'select:not([disabled]), ' +
        'textarea:not([disabled]):not([readonly]), ' +
        'button:not([disabled])';

      const focusableElements = Array.from(
        editFormRef.current?.querySelectorAll(focusableSelector) || []
      );

      if (focusableElements.length === 0) return;
      
      const currentIndex = focusableElements.indexOf(document.activeElement);

      if (currentIndex === focusableElements.length - 1) {
        e.preventDefault();
        const form = document.getElementById("editExpenseForm");
        if(form) form.requestSubmit();
        return;
      }

      if (e.target.tagName === 'TEXTAREA') return;

      if (currentIndex >= 0) {
        e.preventDefault();
        focusableElements[currentIndex + 1]?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showEditModal]);

  const handlePageChange = (page) => { applyPagination(allExpenses, page, rowsPerPage || 15); };

  const effectiveRows = rowsPerPage || 15;
  const totalPages = Math.ceil(totalRecords / effectiveRows);

  const fetchData = async () => {
    try {
       
        
       const accRes = await customFetch(`/AccountMaster`);
        const accData = await accRes.json();
        setRawAccounts(accData);
        setAccountOptions(accData.map(acc => ({ value: acc.name, label: acc.name, id: acc.amId })));

        const bankRes = await customFetch(`/BankDT`);
        const bankData = await bankRes.json();
        setRawBanks(bankData);
        setBankOptions(bankData.map(bank => ({ value: bank.bName, label: bank.bName, id: bank.bid })));

       const docRes = await customFetch(`/Employees/doctors`);
        const docData = await docRes.json();
        setDoctorOptions(docData.map(d => {
          let lbl = d.name;
          if (d.degree) lbl += ` (${d.degree})`;
          if (d.department?.departmentName) lbl += ` - ${d.department.departmentName}`;
          return { value: lbl, label: lbl };
        }));

        fetchExpenses();
    } catch (e) { setError("Failed to load initial data"); }
  };

  const fetchExpenses = () => {
    const params = new URLSearchParams();
    if (formData.fromDate) params.append("fromDate", formData.fromDate);
    if (formData.toDate) params.append("toDate", formData.toDate);
    if (formData.accountName) params.append("accountName", formData.accountName);
    if (formData.bankName) params.append("bankName", formData.bankName);

    setLoading(true);
  customFetch(`/ExPDT/Search?${params.toString()}`)
    .then((res) => { if (!res.ok) throw new Error("Failed to fetch"); return res.json(); })
      .then((data) => {
        const sortedData = data.sort((a, b) => b.expID - a.expID);
        setAllExpenses(sortedData); 
        setTotalRecords(sortedData.length);
        applyPagination(sortedData, currentPage, rowsPerPage || 15); 
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to load expenses"); setAllExpenses([]); setDisplayExpenses([]);
        setTotalRecords(0); setLoading(false);
      });
  };
  useEffect(() => { fetchData(); }, []);

  const handleSearch = (e) => { e.preventDefault(); setCurrentPage(1); fetchExpenses(); };
  
  const handleSearchSelectChange = (name, selectedOption) => {
      setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : "" }));
  };

  const handleInputChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // LOAD IPD/OPD PATIENTS for Modal AsyncCreatableSelect
  const loadPatientOptions = async (inputValue) => {
    if (!inputValue) return [];
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const d = new Date(dateString);
        return isNaN(d.getTime()) ? dateString : d.toLocaleDateString('en-IN');
      };

     const ipdPromise = customFetch(`/Ipds/list?search=${encodeURIComponent(inputValue)}`)
        .then(res => res.json())
        .catch(() => []);

    const opdPromise = customFetch(`/Opds/list?search=${encodeURIComponent(inputValue)}&useDateFilter=false`)
        .then(res => res.json())
        .then(data => data.data || []) 
        .catch(() => []);

      const [ipdData, opdData] = await Promise.all([ipdPromise, opdPromise]);

      const ipdOptions = (Array.isArray(ipdData) ? ipdData : []).map(item => {
        const uhid = item.uhiD_No || item.uhidNo || "N/A";
        const date = formatDate(item.regDate || item.ipD_Date);
        return {
          label: `[IPD] ${item.name} | Date: ${date} | UHID: ${uhid} | Mob: ${item.mobileNo} | IPD No: ${item.ipD_SRNO}`,
          value: item.name,
          originalData: { ...item, type: 'IPD', refId: item.ipD_ID }
        };
      });

      const opdOptions = (Array.isArray(opdData) ? opdData : []).map(item => {
        const uhid = item.uhidNo || item.uhiD_No || "N/A";
        const date = formatDate(item.regDate || item.opdDate);
        return {
          label: `[OPD] ${item.name} | Date: ${date} | UHID: ${uhid} | Mob: ${item.mobileNo} | OPD No: ${item.opD_SRNO}`,
          value: item.name,
          originalData: { ...item, type: 'OPD', refId: item.opdId }
        };
      });

      return [...ipdOptions, ...opdOptions];
    } catch { 
      return []; 
    }
  };

  // ✅ UPDATED: OPEN EDIT MODAL AND FETCH FULL PATIENT DETAILS
  const handleEditClick = async (expense) => {
    // Basic label banate hain pehle (jab tak API fetch ho rahi ho)
    let initialLabel = expense.patientType && expense.patientType !== "Manual" 
      ? `[${expense.patientType}] ${expense.patientName}` 
      : expense.patientName || "";

    // Turant form open kar do basic data ke sath
    setEditForm({
      expID: expense.expID,
      amID: expense.amID, 
      bid: expense.bid, 
      accountName: rawAccounts.find((a) => a.amId === expense.amID)?.name || "",
      bankName: rawBanks.find((b) => b.bid === expense.bid)?.bName || "",
      exDate: expense.exDate ? expense.exDate.slice(0, 10) : "",
      amount: expense.amount || "",
      payMode: expense.payMode || "Cash",
      chqNo: expense.chqNo || "",
      chqDate: expense.chqdate ? expense.chqdate.slice(0, 10) : "",
      remarks: expense.remarks || "", 
      patientName: expense.patientName || "",
      doctorName: expense.doctorName || "",
      patientType: expense.patientType || "", 
      refId: expense.refId || null,
      patientFullLabel: expense.refId ? `${initialLabel} (Fetching details...)` : initialLabel
    });
    
    setError(""); setSuccess("");
    setShowEditModal(true);

   if (expense.refId && (expense.patientType === "IPD" || expense.patientType === "OPD")) {
      try {
        const endpoint = expense.patientType === "IPD" ? `/Ipds/${expense.refId}` : `/Opds/${expense.refId}`;
        const res = await customFetch(endpoint);
        
        if (res.ok) {
          const data = await res.json();
          const d = new Date(data.regDate || data.ipD_Date || data.opdDate);
          const dateStr = isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString('en-IN');
          const uhid = data.uhiD_No || data.uhidNo || "N/A";
          const srNo = data.ipD_SRNO || data.opD_SRNO || expense.refId;

          const fullLabel = `[${expense.patientType}] ${data.name} | Date: ${dateStr} | UHID: ${uhid} | Mob: ${data.mobileNo} | No: ${srNo}`;
          
          setEditForm(prev => ({ ...prev, patientFullLabel: fullLabel }));
        } else {
          setEditForm(prev => ({ ...prev, patientFullLabel: initialLabel })); // Fail hua toh wapas basic pe le aao
        }
      } catch (err) {
        setEditForm(prev => ({ ...prev, patientFullLabel: initialLabel }));
      }
    }
  };

  const handleEditFormChange = (e) => setEditForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  
  const handleEditSelectChange = (name, selectedOption) => {
    setEditForm(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : "" }));
  };

  // Modal me jab naya patient select ho
  const handleEditPatientChange = (selectedOption) => {
    if (selectedOption && selectedOption.originalData) {
      setEditForm(prev => ({ 
        ...prev, 
        patientName: selectedOption.value,
        patientType: selectedOption.originalData.type,
        refId: selectedOption.originalData.refId,
        patientFullLabel: selectedOption.label
      }));
    } else if (selectedOption) {
      setEditForm(prev => ({ 
        ...prev, 
        patientName: selectedOption.value,
        patientType: "Manual",
        refId: null,
        patientFullLabel: selectedOption.label
      }));
    } else {
      setEditForm(prev => ({ 
        ...prev, 
        patientName: "", 
        patientType: "", 
        refId: null,
        patientFullLabel: ""
      }));
    }
  };

  // SUBMIT UPDATE FROM MODAL
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.accountName || !editForm.amount || !editForm.exDate) { 
        setError("Account Type, Date and Amount are required."); 
        return; 
    }
    if (editForm.payMode === "Cheque" && (!editForm.chqNo || !editForm.chqDate)) { setError("Cheque details required."); return; }

    setLoading(true); setError(""); setSuccess("");

    try {
      const selectedAccount = rawAccounts.find((a) => a.name === editForm.accountName);
      const selectedBank = rawBanks.find((b) => b.bName === editForm.bankName);

      const payload = {
        ExpID: editForm.expID, 
        AmID: selectedAccount?.amId, 
        BID: selectedBank ? selectedBank.bid : 0,
        ExDate: editForm.exDate, 
        Remarks: editForm.remarks, 
        Amount: parseFloat(editForm.amount),
        PayMode: editForm.payMode,
        ChqNo: editForm.payMode === "Cheque" ? editForm.chqNo : null,
        Chqdate: editForm.payMode === "Cheque" ? editForm.chqDate : null,
        PatientName: editForm.patientName, 
        DoctorName: editForm.doctorName,
        PatientType: editForm.patientType, 
        RefId: editForm.refId ? Number(editForm.refId) : null,
        EditedBy: "", EditedOn: new Date().toISOString(),
      };

      const res = await customFetch(`/ExPDT/${editForm.expID}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text() || "Update failed.");
      
      setSuccess("Updated successfully!"); 
      setShowEditModal(false);
      setLatestEntryId(editForm.expID); 
      fetchExpenses();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const handleDelete = async (expID) => {
    if (!window.confirm("Delete this expense?")) return;
    setLoading(true); setError(""); setSuccess("");

   try {
      const res = await customFetch(`/ExPDT/${expID}`, {
        method: "DELETE" 
      });
      if (!res.ok) throw new Error(await res.text() || "Delete failed.");
      setSuccess("Deleted successfully!"); fetchExpenses();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const formatNumber = (num) => (typeof num === "number" ? num.toFixed(2) : num || "");

  const renderPagination = () => {
    if (!effectiveRows || totalRecords === 0) return null;
    const pageNumbers = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

    return (
      <div className="dc-pagination">
        <button className="dc-btn-page" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}><i className="bi bi-chevron-left"></i></button>
        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(num => (
            <button key={num} className={`dc-btn-page ${currentPage === num ? "active" : ""}`} onClick={() => handlePageChange(num)}>{num}</button>
        ))}
        <button className="dc-btn-page" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}><i className="bi bi-chevron-right"></i></button>
      </div>
    );
  };

  return (
    <div className="dc-wrap">
      <div className="dc-card">
        {/* Compact Header */}
        <div className="dc-header">
          <div className="dc-header-inner">
            <div className="dc-header-brand">
              <div className="dc-header-icon"><i className="bi bi-search"></i></div>
              <span className="dc-header-title">Expense Search</span>
            </div>
          </div>
        </div>

        <div className="dc-body">
          {error && <div className="dc-alert dc-alert-danger"><i className="bi bi-exclamation-triangle-fill"></i> {error}</div>}
          {success && <div className="dc-alert dc-alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

          {/* Compact Filters */}
          <div className="dc-section">
            <div className="dc-section-title"><i className="bi bi-funnel"></i><span>Filters</span></div>
            <div className="dc-section-body">
              <form onSubmit={handleSearch} className="dc-grid dc-grid-auto">
                <div>
                  <label className="dc-label">From Date</label>
                  <input type="date" name="fromDate" className="dc-input" value={formData.fromDate} onChange={handleInputChange} max={formData.toDate} />
                </div>
                <div>
                  <label className="dc-label">To Date</label>
                  <input type="date" name="toDate" className="dc-input" value={formData.toDate} onChange={handleInputChange} min={formData.fromDate} />
                </div>
                <div>
                  <label className="dc-label">Expense Type</label>
                  <Select options={accountOptions} value={accountOptions.find(opt => opt.value === formData.accountName) || null} onChange={(opt) => handleSearchSelectChange('accountName', opt)} placeholder="All Types" isClearable styles={selectStyles} menuPortalTarget={document.body} />
                </div>
                <div>
                  <label className="dc-label">Bank Name</label>
                  <Select options={bankOptions} value={bankOptions.find(opt => opt.value === formData.bankName) || null}
                   onChange={(opt) => handleSearchSelectChange('bankName', opt)}
                   placeholder="All Banks" isClearable styles={selectStyles} menuPortalTarget={document.body} />
                </div>
                <div style={{ maxWidth: '80px' }}>
                  <label className="dc-label">Rows</label>
                  <input type="number" className="dc-input" value={rowsPerPage} min="1" max="100" onChange={handleRowsPerPageChange} />
                </div>
                <div className="dc-align-end" style={{ paddingBottom: '1px' }}>
                  <button type="submit" className="dc-btn-submit" disabled={loading}>{loading ? "..." : "Search"}</button>
                </div>
              </form>
            </div>
          </div>

          <div className="dc-records-info">
              Showing {((currentPage - 1) * effectiveRows) + 1} to {Math.min(currentPage * effectiveRows, totalRecords)} of {totalRecords} entries
          </div>

          {/* Compact Table */}
          <div className="dc-table-wrap">
            <table className="dc-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Account</th>
                  <th>Bank</th>
                  <th>Date</th>
                  <th>Pay Mode</th>
                  <th>Patient Name</th>
                  <th>Remarks</th>
                  <th className="dc-text-right">Amount</th>
                  <th className="dc-text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayExpenses.length === 0 ? (
                  <tr><td colSpan="10" className="dc-empty">No expenses found.</td></tr>
                ) : (
                  displayExpenses.map((exp) => (
                    <tr key={exp.expID} className={latestEntryId === exp.expID ? "dc-highlight-row" : ""}>
                      <td className="dc-text-muted">{exp.expID}</td>
                      <td>{rawAccounts.find(a => a.amId === exp.amID)?.name || exp.amID}</td>
                      <td>{rawBanks.find(b => b.bid === exp.bid)?.bName || exp.bid}</td>
                      <td>{exp.exDate ? new Date(exp.exDate).toLocaleDateString() : "-"}</td>
                      <td><span className="dc-badge">{exp.payMode || "-"}</span></td>
                      {/* Patient Name with optional Badge */}
                      <td>
                        {exp.patientName || "-"} 
                        {exp.patientType && exp.patientType !== "Manual" && <span style={{fontSize: "9px", color: "#6c757d", marginLeft: "4px"}}>({exp.patientType})</span>}
                      </td>
                      <td>{exp.doctorName || "-"}</td>
                      <td className="dc-text-right dc-amount">₹{formatNumber(exp.amount)}</td>
                      <td className="dc-text-center">
                        <div className="dc-btn-group">
                          <button className="dc-icon-btn dc-edit" onClick={() => handleEditClick(exp)}><i className="bi bi-pencil-square"></i></button>
                          <button className="dc-icon-btn dc-delete" onClick={() => handleDelete(exp.expID)}><i className="bi bi-trash"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && renderPagination()}
        </div>
      </div>

      {/* ✅ COMPACT EDIT MODAL - WIDER AND MATCHES CREATE FORM */}
      {showEditModal && (
        <div className="dc-modal-overlay">
          <div className="dc-modal-content">
            <div className="dc-modal-header">
              <div className="dc-header-brand">
                <i className="bi bi-pencil-square"></i> <span>Edit Expense (ID: {editForm.expID})</span>
              </div>
              <button className="dc-modal-close" onClick={() => setShowEditModal(false)}><i className="bi bi-x-lg"></i></button>
            </div>
            
            <div className="dc-modal-body">
              <form id="editExpenseForm" ref={editFormRef} onSubmit={handleUpdateSubmit}> 
                
                {/* ROW 1: Expense Type | Patient (OPD/IPD) | Remarks (Doctor) */}
                <div className="dc-grid dc-grid-row1 dc-mb2">
                  <div>
                    <label className="dc-label">Expense Type <span className="dc-required">*</span></label>
                    <Select 
                      autoFocus={true}  
                      options={accountOptions} 
                      value={accountOptions.find(opt => opt.value === editForm.accountName)} 
                      onChange={(opt) => handleEditSelectChange('accountName', opt)} 
                      styles={selectStyles} 
                      menuPortalTarget={document.body} 
                    />
                  </div>
                  <div>
                    <label className="dc-label">Patient (OPD/IPD) <span className="dc-required">*</span></label>
                    <AsyncCreatableSelect 
                      cacheOptions defaultOptions 
                      loadOptions={loadPatientOptions} 
                      onChange={handleEditPatientChange} 
                      value={editForm.patientName ? { label: editForm.patientFullLabel || editForm.patientName, value: editForm.patientName } : null}
                      placeholder="Search or Type Name..." 
                      isClearable styles={selectStyles} menuPortalTarget={document.body} 
                    />
                  </div>
                  <div>
                    <label className="dc-label">Remarks (or search doctor)</label>
                    <CreatableSelect 
                      options={doctorOptions} 
                      onChange={(opt) => handleEditSelectChange('doctorName', opt)} 
                      value={editForm.doctorName ? { label: editForm.doctorName, value: editForm.doctorName } : null}
                      placeholder="Search or Type Name..." 
                      isClearable styles={selectStyles} menuPortalTarget={document.body} 
                    />
                  </div>
                </div>

                {/* ROW 2: Expense Date | Bank Account | Amount | Payment Mode */}
                <div className="dc-grid dc-grid-row2 dc-mb2">
                  <div>
                    <label className="dc-label">Expense Date <span className="dc-required">*</span></label>
                    <input type="date" name="exDate" value={editForm.exDate} onChange={handleEditFormChange} className="dc-input" required />
                  </div>
                  <div>
                    <label className="dc-label">Bank Account</label>
                    <Select 
                      options={bankOptions} 
                      value={bankOptions.find(opt => opt.value === editForm.bankName) || null} 
                      onChange={(opt) => handleEditSelectChange('bankName', opt)} 
                      styles={selectStyles} 
                      menuPortalTarget={document.body} 
                      isClearable={true} 
                      placeholder="Select Bank"
                    />
                  </div>
                  <div>
                    <label className="dc-label">Amount (₹) <span className="dc-required">*</span></label>
                    <input type="number" name="amount" value={editForm.amount} onChange={handleEditFormChange} className="dc-input" step="0.01" required />
                  </div>
                  <div>
                    <label className="dc-label">Payment Mode <span className="dc-required">*</span></label>
                    <Select options={payModeOptions} value={payModeOptions.find(opt => opt.value === editForm.payMode)} onChange={(opt) => handleEditSelectChange('payMode', opt)} styles={selectStyles} menuPortalTarget={document.body} />
                  </div>
                </div>

                {/* ROW 3: Cheque Fields */}
                {editForm.payMode === "Cheque" && (
                  <div className="dc-grid dc-grid-3 dc-mb2">
                    <div>
                      <label className="dc-label">Cheque No <span className="dc-required">*</span></label>
                      <input type="text" name="chqNo" value={editForm.chqNo} onChange={handleEditFormChange} className="dc-input" required />
                    </div>
                    <div>
                      <label className="dc-label">Cheque Date <span className="dc-required">*</span></label>
                      <input type="date" name="chqDate" value={editForm.chqDate} onChange={handleEditFormChange} className="dc-input" required />
                    </div>
                  </div>
                )}
              </form>
            </div>
            
            <div className="dc-modal-footer">
              <button type="button" className="dc-btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button type="submit" form="editExpenseForm" className="dc-btn-submit" disabled={loading}>{loading ? "Saving..." : "Save Changes"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ PURE COMPACT CSS (ZERO BOOTSTRAP OVERRIDES) */}
      <style jsx>{`
        *, *::before, *::after { box-sizing: border-box; }
        .dc-wrap { padding: 0; font-family: system-ui, sans-serif; }
        
        .dc-card { background: #fff; border-radius: 6px; box-shadow: 0 1px 8px rgba(0,0,0,0.06); border: 1px solid #e0e6ed; }
        
        .dc-header { background: var(--theme-color, #0088cc); padding: 6px 12px; border-radius: 6px 6px 0 0; border-bottom: 2px solid rgba(0,0,0,0.1); }
        .dc-header-inner { display: flex; align-items: center; }
        .dc-header-brand { display: flex; align-items: center; gap: 6px; color: white; font-weight: 600; font-size: 13px; }
        
        .dc-body { padding: 10px 12px; }
        
        .dc-section { background: #f8f9fa; border: 1px solid #e0e6ed; border-radius: 5px; margin-bottom: 10px; }
        .dc-section-title { background: var(--theme-color, #0088cc); padding: 5px 10px; display: flex; align-items: center; gap: 5px; border-radius: 4px 4px 0 0; color: white; font-size: 11px; font-weight: 600; }
        .dc-section-body { padding: 8px 10px; background: #fff; border-radius: 0 0 5px 5px; }
        
        .dc-grid { display: grid; gap: 8px; align-items: start; }
        .dc-grid-auto { grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); }
        
        /* ✅ ROW 1 WIDTH ADJUSTED: Patient gets 3.5x space, Doctor is smaller */
        .dc-grid-row1 { grid-template-columns: 1.2fr 3.5fr 1.5fr; } 
        .dc-grid-row2 { grid-template-columns: repeat(4, 1fr); } 

        .dc-grid-3 { grid-template-columns: repeat(3, 1fr); }
        .dc-grid-1 { grid-template-columns: 1fr; }
        .dc-mb2 { margin-bottom: 10px; }
        .dc-align-end { align-self: end; }

        .dc-label { display: block; font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 2px; }
        .dc-required { color: #dc2626; }
        
        .dc-input, .dc-textarea { width: 100%; height: 26px; min-height: 26px; padding: 2px 6px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; outline: none; }
        .dc-input:focus, .dc-textarea:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0,136,204,0.15); }
        .dc-textarea { resize: vertical; height: auto; min-height: 40px; padding-top: 4px; }

        .dc-btn-submit { height: 26px; padding: 0 14px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; }
        .dc-btn-submit:hover { opacity: 0.9; }
        .dc-btn-cancel { height: 26px; padding: 0 14px; background: #f3f4f6; color: #495057; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; }
        
        .dc-records-info { font-size: 10px; color: #7f8c8d; margin-bottom: 6px; }

        .dc-table-wrap { overflow-x: auto; border: 1px solid #e0e6ed; border-radius: 4px; }
        .dc-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .dc-table th { background: var(--theme-color, #0088cc); color: white; padding: 6px 8px; font-weight: 600; text-align: left; white-space: nowrap; }
        .dc-table td { padding: 5px 8px; border-bottom: 1px solid #e0e6ed; vertical-align: middle; }
        .dc-table tr:hover { background: #f0f7ff; }
        .dc-highlight-row { background-color: #d1e7dd !important; }
        
        .dc-text-right { text-align: right; }
        .dc-text-center { text-align: center; }
        .dc-text-muted { color: #6c757d; }
        .dc-amount { color: #d93025; font-weight: 600; }
        .dc-badge { background: #e9ecef; padding: 2px 6px; border-radius: 4px; font-size: 10px; }
        
        .dc-btn-group { display: flex; gap: 4px; justify-content: center; }
        .dc-icon-btn { width: 22px; height: 22px; border-radius: 3px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; }
        .dc-edit { background: #e0f2fe; color: #0284c7; }
        .dc-edit:hover { background: #0284c7; color: white; }
        .dc-delete { background: #fee2e2; color: #dc2626; }
        .dc-delete:hover { background: #dc2626; color: white; }

        .dc-pagination { display: flex; justify-content: center; gap: 4px; margin-top: 10px; }
        .dc-btn-page { width: 24px; height: 24px; border: 1px solid #cbd5e0; background: white; border-radius: 4px; cursor: pointer; font-size: 11px; }
        .dc-btn-page:hover { background: #f1f3f5; }
        .dc-btn-page.active { background: var(--theme-color, #0088cc); color: white; border-color: var(--theme-color, #0088cc); }

        .dc-alert { padding: 6px 10px; border-radius: 4px; font-size: 11px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .dc-alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .dc-alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }

        /* ✅ MODAL WIDTH INCREASED TO 900px TO FIT FULL PATIENT LABELS */
        .dc-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1050; backdrop-filter: blur(2px); }
        .dc-modal-content { background: white; width: 900px; max-width: 95%; border-radius: 6px; box-shadow: 0 10px 30px rgba(0,0,0,0.2); animation: slideIn 0.2s ease; overflow: visible; }
        .dc-modal-header { background: var(--theme-color, #0088cc); padding: 8px 14px; border-radius: 6px 6px 0 0; display: flex; justify-content: space-between; align-items: center; color: white; }
        .dc-modal-close { background: none; border: none; color: white; cursor: pointer; font-size: 14px; opacity: 0.8; }
        .dc-modal-close:hover { opacity: 1; }
        .dc-modal-body { padding: 14px; }
        .dc-modal-footer { padding: 10px 14px; background: #f8f9fa; border-top: 1px solid #e0e6ed; border-radius: 0 0 6px 6px; display: flex; justify-content: flex-end; gap: 8px; }
        
        @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

        @media (max-width: 900px) {
          .dc-grid-row1, .dc-grid-row2 { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 600px) {
          .dc-grid-row1, .dc-grid-row2, .dc-grid-3 { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default ExPDTSearchForm;